/**
 * Properties Page
 *
 * Deduplicated property database view. Each record represents a unique
 * Australian property address, automatically populated and updated
 * as crawl jobs process URLs.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Search,
  Home,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Loader2,
  ExternalLink,
  Bed,
  Bath,
  Car,
  DollarSign,
  MapPin,
  Building2,
  X,
  History,
  Tag,
  ArrowUpDown,
  Navigation,
} from "lucide-react";
import { Button } from "@repo/ui/button";
import { Input } from "@repo/ui/input";
import { Badge } from "@repo/ui/badge";
import { Card, CardContent } from "@repo/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@repo/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@repo/ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/ui/tooltip";
import { cn } from "@repo/ui/lib/utils";
import { formatDate } from "@/lib/formatDate";
import {
  fetchProperties,
  fetchPropertySnapshots,
  getPropertyStatsSimple,
  getPropertyTypes,
  getListingTypes,
  normalizePropertyType,
} from "@/lib/api/propertyDatabase";
import type { Property, PropertySnapshot, PropertyFilters } from "@/lib/api/propertyDatabase";
import { openStoredPage } from "@/lib/api";
import { ScrollArea } from "@repo/ui/scroll-area";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

const STATES = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'];
const PAGE_SIZE = 50;

function formatPrice(price: number | null): string {
  if (!price) return "-";
  if (price >= 1_000_000) return `$${(price / 1_000_000).toFixed(1)}M`;
  if (price >= 1_000) return `$${(price / 1_000).toFixed(0)}K`;
  return `$${price.toLocaleString()}`;
}

function normalizePriceDisplay(priceDisplay: string | null): string | null {
  if (!priceDisplay) return null;
  const normalized = priceDisplay.trim();
  if (!normalized) return null;
  const lower = normalized.toLowerCase();
  if (lower === "null" || lower === "undefined" || lower === "-") return null;
  return normalized;
}

function getPriceFallbackLabel(listingType: string | null, priceDisplay: string | null): string {
  const safePriceDisplay = normalizePriceDisplay(priceDisplay);
  if (safePriceDisplay) return safePriceDisplay;
  if (listingType?.toLowerCase() === "sold") return "Undisclosed / Contact Agent";
  if (listingType?.toLowerCase() === "buy") return "Contact Agent";
  return "-";
}

function formatTimeAgo(dateStr: string | null): string {
  if (!dateStr) return "-";
  const now = new Date();
  const date = new Date(dateStr);
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

const Properties = () => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({ total: 0, withPrice: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [propertyTypes] = useState<string[]>(() => getPropertyTypes());
  const [listingTypes] = useState<{ value: string; label: string }[]>(() => getListingTypes());
  const [sortBy, setSortBy] = useState<'updated_at' | 'last_scraped_at'>('updated_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [stateFilter, setStateFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [listingTypeFilter, setListingTypeFilter] = useState<string>("all");
  const [postcodeFilter, setPostcodeFilter] = useState("");

  const filters = useMemo<PropertyFilters>(() => ({
    search: searchQuery || undefined,
    state: stateFilter !== "all" ? stateFilter : undefined,
    propertyType: typeFilter !== "all" ? typeFilter : undefined,
    listingType: listingTypeFilter !== "all" ? listingTypeFilter : undefined,
    postcode: postcodeFilter.length === 4 ? postcodeFilter : undefined,
  }), [searchQuery, stateFilter, typeFilter, listingTypeFilter, postcodeFilter]);
  const filtersResetKey = `${searchQuery}|${stateFilter}|${typeFilter}|${listingTypeFilter}|${postcodeFilter}`;

  const loadProperties = useCallback(async () => {
    try {
      setIsLoading(true);
      setLoadError(null);
      const offset = (currentPage - 1) * PAGE_SIZE;

      const [result, statsResult] = await Promise.all([
        fetchProperties({ filters, limit: PAGE_SIZE, offset, sortBy, sortOrder }),
        getPropertyStatsSimple(),
      ]);

      setProperties(result.data);
      setTotalCount(result.count);
      setStats(statsResult);
    } catch (error) {
      console.error("Error loading properties:", error);
      setLoadError("Could not load properties for the current filters. Try adjusting your search.");
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, sortBy, sortOrder, filters]);

  // Load on mount and filter changes
  useEffect(() => {
    loadProperties();
  }, [loadProperties]);

  // Property types and listing types are derived from predefined canonical
  // maps (synchronous), so no useEffect needed for loading them.

  // Reset page on filter change
  useEffect(() => {
    if (filtersResetKey) {
      setCurrentPage(1);
    }
  }, [filtersResetKey]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const clearFilters = () => {
    setSearchQuery("");
    setStateFilter("all");
    setTypeFilter("all");
    setListingTypeFilter("all");
    setPostcodeFilter("");
  };

  const hasActiveFilters = searchQuery || stateFilter !== "all" || typeFilter !== "all" || listingTypeFilter !== "all" || postcodeFilter;

  // "Geocode missing on this page" — fires the normalize-address edge fn
  // for each visible property without a place_id. Bulk backfill across the
  // whole DB runs separately via the backfill-geocoding Firebase function.
  const [isGeocoding, setIsGeocoding] = useState(false);
  const geocodeMissingOnPage = useCallback(async () => {
    const targets = properties.filter((p) => !p.place_id);
    if (targets.length === 0) return;
    setIsGeocoding(true);
    try {
      for (const p of targets) {
        await supabase.functions.invoke("normalize-address", {
          body: {
            rawAddress: p.full_address,
            suburb: p.suburb,
            state: p.state,
            postcode: p.postcode,
            propertyId: p.id,
          },
        });
      }
      await loadProperties();
    } finally {
      setIsGeocoding(false);
    }
  }, [properties, loadProperties]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Property Database</h1>
          <p className="text-muted-foreground">
            Deduplicated Australian property records from all crawl jobs
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="py-1.5">
            <Home className="w-3.5 h-3.5 mr-1" />
            {stats.total.toLocaleString()} properties
          </Badge>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={geocodeMissingOnPage}
            disabled={isGeocoding || isLoading}
            className="gap-1.5"
          >
            {isGeocoding ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Navigation className="w-3.5 h-3.5" />
            )}
            Geocode missing
          </Button>
          <Button variant="outline" size="icon" onClick={loadProperties} disabled={isLoading}>
            <RefreshCw className={cn("w-4 h-4", isLoading && "animate-spin")} />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Total Properties</p>
            <p className="text-2xl font-bold mt-1">{stats.total.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">With Price Data</p>
            <p className="text-2xl font-bold mt-1 text-green-600 dark:text-green-400">
              {stats.withPrice.toLocaleString()}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Showing</p>
            <p className="text-2xl font-bold mt-1">{totalCount.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">With Price Rate</p>
            <p className="text-2xl font-bold mt-1 text-blue-600 dark:text-blue-400">
              {stats.total > 0
                ? `${Math.round((stats.withPrice / stats.total) * 100)}%`
                : "-"
              }
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="property-search"
                name="propertySearch"
                placeholder="Search address, suburb, or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={stateFilter} onValueChange={setStateFilter}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="State" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All States</SelectItem>
                {STATES.map((state) => (
                  <SelectItem key={state} value={state}>{state}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Property Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {propertyTypes.map((type) => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={listingTypeFilter} onValueChange={setListingTypeFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Listing" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Listings</SelectItem>
                {listingTypes.map((lt) => (
                  <SelectItem key={lt.value} value={lt.value}>{lt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              id="property-postcode"
              name="propertyPostcode"
              placeholder="Postcode"
              value={postcodeFilter}
              onChange={(e) => setPostcodeFilter(e.target.value.replace(/\D/g, '').slice(0, 4))}
              className="w-[100px]"
              maxLength={4}
            />
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1">
                <X className="w-4 h-4" />
                Clear
              </Button>
            )}
          </div>
          {searchQuery && /[%(),'"\\]/.test(searchQuery) && (
            <p className="text-xs text-muted-foreground mt-2">
              Search punctuation is ignored to keep queries valid.
            </p>
          )}
        </CardContent>
      </Card>

      {loadError && (
        <p className="text-sm text-destructive">{loadError}</p>
      )}

      {/* Properties Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[300px]">Address</TableHead>
                <TableHead className="w-[120px]">Suburb</TableHead>
                <TableHead className="w-[100px]">Type</TableHead>
                <TableHead className="w-[60px] text-center">
                  <Bed className="w-4 h-4 mx-auto" />
                </TableHead>
                <TableHead className="w-[60px] text-center">
                  <Bath className="w-4 h-4 mx-auto" />
                </TableHead>
                <TableHead className="w-[60px] text-center">
                  <Car className="w-4 h-4 mx-auto" />
                </TableHead>
                <TableHead className="w-[80px]">Listing</TableHead>
                <TableHead className="w-[120px] text-right">Price</TableHead>
                <TableHead className="w-[50px] text-center">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <History className="w-4 h-4 mx-auto" />
                    </TooltipTrigger>
                    <TooltipContent>Snapshots</TooltipContent>
                  </Tooltip>
                </TableHead>
                <TableHead className="w-[140px]">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="px-2"
                    onClick={() => {
                      if (sortBy !== 'updated_at') {
                        setSortBy('updated_at');
                        setSortOrder('desc');
                        return;
                      }
                      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
                    }}
                  >
                    Last Change
                    <ArrowUpDown className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                    <p className="text-sm text-muted-foreground mt-2">Loading properties...</p>
                  </TableCell>
                </TableRow>
              ) : properties.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-12 text-muted-foreground">
                    <div className="space-y-2">
                      <Home className="w-8 h-8 mx-auto opacity-50" />
                      <p>No properties found</p>
                      <p className="text-sm">
                        {hasActiveFilters
                          ? "Try adjusting your filters"
                          : "Run a scrape job to populate the property database"
                        }
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                properties.map((property) => {
                  const primaryPrice = property.last_sale_price ?? property.price;
                  const hasPrimaryPrice = primaryPrice !== null && primaryPrice !== undefined;

                  return (
                    <TableRow
                      key={property.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => setSelectedProperty(property)}
                    >
                    <TableCell>
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                        <div>
                          <Link
                            to={`/dashboard/properties/${property.id}`}
                            className="font-medium text-sm text-primary hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {property.full_address}
                          </Link>
                          {property.scrape_count > 1 && (
                            <Badge variant="secondary" className="ml-2 text-xs px-1 py-0">
                              {property.scrape_count}x
                            </Badge>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <span className="font-medium">{property.suburb}</span>
                        <span className="text-muted-foreground text-xs ml-1">
                          {property.state} {property.postcode}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {property.property_type ? (
                        <Badge variant="outline" className="text-xs font-normal">
                          {normalizePropertyType(property.property_type)}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center text-sm">
                      {property.bedrooms ?? "-"}
                    </TableCell>
                    <TableCell className="text-center text-sm">
                      {property.bathrooms ?? "-"}
                    </TableCell>
                    <TableCell className="text-center text-sm">
                      {property.parking ?? "-"}
                    </TableCell>
                    <TableCell>
                      {property.listing_type ? (
                        <ListingTypeBadge type={property.listing_type} />
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {hasPrimaryPrice ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="text-right">
                              <span className="font-medium text-sm">
                                {formatPrice(primaryPrice)}
                              </span>
                              {property.last_sale_date && (
                                <p className="text-xs text-muted-foreground">
                                  {new Date(property.last_sale_date).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })}
                                </p>
                              )}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            {normalizePriceDisplay(property.price_display) || `$${primaryPrice.toLocaleString()}`}
                            {property.last_sale_date && ` (sold ${new Date(property.last_sale_date).toLocaleDateString('en-AU')})`}
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <span className="text-muted-foreground text-sm">
                          {getPriceFallbackLabel(property.listing_type, property.price_display)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-center text-xs text-muted-foreground">
                      {property.snapshot_count > 0 ? property.snapshot_count : "-"}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span>{formatTimeAgo(property.updated_at)}</span>
                        </TooltipTrigger>
                        <TooltipContent>
                          {new Date(property.updated_at).toLocaleDateString('en-AU', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </TooltipContent>
                      </Tooltip>
                    </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <div className="text-sm text-muted-foreground">
                Showing {((currentPage - 1) * PAGE_SIZE) + 1} to{" "}
                {Math.min(currentPage * PAGE_SIZE, totalCount)} of{" "}
                {totalCount.toLocaleString()} properties
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1 || isLoading}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages || isLoading}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Property Detail Dialog */}
      <PropertyDetailDialog
        property={selectedProperty}
        onClose={() => setSelectedProperty(null)}
      />
    </div>
  );
};

const listingTypeConfig: Record<string, { label: string; className: string }> = {
  buy: { label: "For Sale", className: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400" },
  sold: { label: "Sold", className: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400" },
  rent: { label: "For Rent", className: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400" },
  auction: { label: "Auction", className: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400" },
};

function ListingTypeBadge({ type }: { type: string }) {
  const config = listingTypeConfig[type.toLowerCase()] || { label: type, className: "bg-gray-100 text-gray-800" };
  return (
    <Badge className={cn("text-xs", config.className)}>
      {config.label}
    </Badge>
  );
}

function PropertyDetailDialog({
  property,
  onClose,
}: {
  property: Property | null;
  onClose: () => void;
}) {
  const [snapshots, setSnapshots] = useState<PropertySnapshot[]>([]);
  const [loadingSnapshots, setLoadingSnapshots] = useState(false);
  const [openingSnapshotId, setOpeningSnapshotId] = useState<string | null>(null);
  const propertyId = property?.id;

  useEffect(() => {
    if (propertyId) {
      setLoadingSnapshots(true);
      fetchPropertySnapshots(propertyId)
        .then(setSnapshots)
        .catch(console.error)
        .finally(() => setLoadingSnapshots(false));
    } else {
      setSnapshots([]);
    }
  }, [propertyId]);

  if (!property) return null;

  const openPageSnapshot = async (snapshot: PropertySnapshot) => {
    if (!snapshot.page_snapshot) return;
    try {
      setOpeningSnapshotId(snapshot.id);
      await openStoredPage(snapshot.page_snapshot);
    } catch (error) {
      console.error("Failed to open page snapshot:", error);
    } finally {
      setOpeningSnapshotId(null);
    }
  };

  return (
    <Dialog open={!!property} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-start gap-2">
            <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <span>{property.full_address}</span>
              {property.listing_type && (
                <span className="ml-2">
                  <ListingTypeBadge type={property.listing_type} />
                </span>
              )}
            </div>
          </DialogTitle>
          <DialogDescription>
            Historical snapshots, pricing context, and source records for this property.{" "}
            <Link
              to={`/dashboard/properties/${property.id}`}
              className="text-primary hover:underline inline-flex items-center gap-1"
            >
              Open full detail page
              <ExternalLink className="w-3 h-3" />
            </Link>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Location */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-muted-foreground uppercase">Suburb</p>
              <p className="font-medium">{property.suburb}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase">State</p>
              <p className="font-medium">{property.state}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase">Postcode</p>
              <p className="font-medium">{property.postcode}</p>
            </div>
          </div>

          {/* Property Details */}
          <div className="grid grid-cols-4 gap-4">
            <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
              <Building2 className="w-4 h-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Type</p>
                <p className="text-sm font-medium">{property.property_type || "-"}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
              <Bed className="w-4 h-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Beds</p>
                <p className="text-sm font-medium">{property.bedrooms ?? "-"}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
              <Bath className="w-4 h-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Baths</p>
                <p className="text-sm font-medium">{property.bathrooms ?? "-"}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
              <Car className="w-4 h-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Parking</p>
                <p className="text-sm font-medium">{property.parking ?? "-"}</p>
              </div>
            </div>
          </div>

          {/* Price / Sale Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {property.listing_type?.toLowerCase() === "sold" && (
              <div className="p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-xs text-muted-foreground">Last Sale Price</p>
                    <p className="font-bold text-lg">
                      {property.last_sale_price
                        ? `$${property.last_sale_price.toLocaleString()}`
                        : getPriceFallbackLabel(property.listing_type, property.price_display)}
                    </p>
                    {property.last_sale_date && (
                      <p className="text-xs text-muted-foreground">
                        Sold {new Date(property.last_sale_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
            {property.listing_type?.toLowerCase() !== "sold" && (property.price || normalizePriceDisplay(property.price_display)) && (
              <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-2">
                  <Tag className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-xs text-muted-foreground">
                      {property.listing_type === 'sold' ? 'Listed Price' : 'Asking Price'}
                    </p>
                    <p className="font-bold text-lg">
                      {property.price
                        ? `$${property.price.toLocaleString()}`
                        : getPriceFallbackLabel(property.listing_type, property.price_display)}
                    </p>
                    {normalizePriceDisplay(property.price_display) && property.price && (
                      <p className="text-xs text-muted-foreground">{normalizePriceDisplay(property.price_display)}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Description */}
          {property.description && (
            <div>
              <p className="text-xs text-muted-foreground uppercase mb-1">Description</p>
              <p className="text-sm leading-relaxed line-clamp-4">{property.description}</p>
            </div>
          )}

          {/* Agency */}
          {property.agency_name && (
            <div>
              <p className="text-xs text-muted-foreground uppercase mb-1">Agency</p>
              <p className="text-sm font-medium">{property.agency_name}</p>
            </div>
          )}

          {/* Snapshot History */}
          <div className="border-t pt-4">
            <div className="flex items-center gap-2 mb-3">
              <History className="w-4 h-4 text-muted-foreground" />
              <p className="text-sm font-semibold">
                Price History & Snapshots ({property.snapshot_count})
              </p>
            </div>

            {loadingSnapshots ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : snapshots.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No historical snapshots yet
              </p>
            ) : (
              <ScrollArea className="max-h-[250px]">
                <div className="space-y-2">
                  {snapshots.map((snap) => (
                    <div
                      key={snap.id}
                      className="flex items-center gap-3 p-2.5 rounded-lg border bg-card text-sm"
                    >
                      {/* Timeline dot */}
                      <div className="flex flex-col items-center gap-1 flex-shrink-0">
                        <div className={cn(
                          "w-2.5 h-2.5 rounded-full",
                          snap.listing_type === 'sold' ? "bg-green-500" : 
                          snap.listing_type === 'buy' ? "bg-blue-500" : "bg-gray-400"
                        )} />
                      </div>

                      {/* Date */}
                      <div className="w-[90px] flex-shrink-0">
                        <p className="font-medium text-xs">
                          {snap.sale_date 
                            ? new Date(snap.sale_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
                            : new Date(snap.snapshot_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
                          }
                        </p>
                        {snap.sale_date && (
                          <p className="text-xs text-muted-foreground">Sale date</p>
                        )}
                        <p className="text-[10px] text-muted-foreground">
                          {formatDate(snap.snapshot_date, { includeTime: true })}
                        </p>
                      </div>

                      {/* Listing type */}
                      <div className="w-[70px] flex-shrink-0">
                        {snap.listing_type ? (
                          <ListingTypeBadge type={snap.listing_type} />
                        ) : (
                          <span className="text-muted-foreground text-xs">-</span>
                        )}
                      </div>

                      {/* Price */}
                      <div className="flex-1">
                        {(snap.sale_price || snap.asking_price) ? (
                          <div>
                            <span className="font-semibold">
                              {formatPrice(snap.sale_price || snap.asking_price)}
                            </span>
                            {normalizePriceDisplay(snap.price_display) && (
                              <span className="text-xs text-muted-foreground ml-1">
                                ({normalizePriceDisplay(snap.price_display)})
                              </span>
                            )}
                          </div>
                        ) : normalizePriceDisplay(snap.price_display) ? (
                          <span className="text-muted-foreground">{normalizePriceDisplay(snap.price_display)}</span>
                        ) : snap.listing_type?.toLowerCase() === "sold" ? (
                          <span className="text-muted-foreground">Undisclosed / Contact Agent</span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </div>

                      {/* Source */}
                      <div className="flex-shrink-0 text-right space-y-1">
                        {snap.adapter_slug && (
                          <Badge variant="outline" className="text-xs">
                            {snap.adapter_slug}
                          </Badge>
                        )}
                        {(snap.job?.id || snap.job_run?.id) && (
                          <div className="text-[10px] text-muted-foreground">
                            {snap.job?.id ? (
                              <a
                                href={`/dashboard/jobs?jobId=${snap.job.id}`}
                                className="hover:underline"
                              >
                                Job {snap.job.id.slice(0, 8)}
                              </a>
                            ) : (
                              <span>Job —</span>
                            )}
                            {snap.job_run?.id && (
                              <span className="block">Run {snap.job_run.id.slice(0, 8)}</span>
                            )}
                          </div>
                        )}
                        {snap.page_snapshot ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2 text-[10px]"
                            onClick={() => openPageSnapshot(snap)}
                            disabled={openingSnapshotId === snap.id}
                          >
                            {openingSnapshotId === snap.id ? (
                              <Loader2 className="w-3 h-3 animate-spin mr-1" />
                            ) : (
                              <ExternalLink className="w-3 h-3 mr-1" />
                            )}
                            Snapshot
                          </Button>
                        ) : (
                          <span className="text-[10px] text-muted-foreground">No page snapshot</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>

          {/* Source Info */}
          <div className="border-t pt-4 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Times Scraped</span>
              <Badge variant="secondary">{property.scrape_count}</Badge>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">First Seen</span>
              <span>{new Date(property.first_seen_at).toLocaleDateString('en-AU')}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Last Scraped</span>
              <span>
                {property.last_scraped_at
                  ? new Date(property.last_scraped_at).toLocaleDateString('en-AU')
                  : "-"
                }
              </span>
            </div>
            {property.source_adapters.length > 0 && (
              <div className="flex justify-between text-sm items-start">
                <span className="text-muted-foreground">Sources</span>
                <div className="flex flex-wrap gap-1 justify-end">
                  {property.source_adapters.map((adapter) => (
                    <Badge key={adapter} variant="outline" className="text-xs">
                      {adapter}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {property.source_urls.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground uppercase mb-1">Source URLs</p>
                <div className="space-y-1">
                  {property.source_urls.slice(0, 3).map((url) => (
                    <a
                      key={url}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-blue-500 hover:underline truncate"
                    >
                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                      {url}
                    </a>
                  ))}
                  {property.source_urls.length > 3 && (
                    <p className="text-xs text-muted-foreground">
                      +{property.source_urls.length - 3} more
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default Properties;
